import { useEffect, useState } from "react";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { getProviderPostAuthDestination } from "@/lib/providerAccessNavigation";
import { createProviderCheckoutSession } from "@/app/api/provider/checkout";
import {
  getProviderBillingSummary,
  openProviderBillingPortal,
} from "@/app/api/provider/billing";
import { validateAuthToken } from "@/guards/validateAuthToken";
import { store } from "@/app/store";
import { getProvider } from "@/app/store/provider/providerSlice";
import { bgLinearGradient } from "@/app/types/ui";
import { LanguageSelector } from "@/components/shared/LanguageSelector";
import { MimiatHorizontalLogo } from "@/components/shared/MimiatHorizonalLogo";
import { Button } from "@/components/ui/Button";

export const Route = createFileRoute("/register/checkout")({
  beforeLoad: async () => {
    validateAuthToken();
    const destination = await getProviderPostAuthDestination();
    if (destination !== "/register/checkout") {
      if (destination === "/dashboard") {
        await store.dispatch(getProvider()).unwrap();
      }
      throw redirect({ to: destination });
    }
  },
  component: ClinicianCheckout,
});

function ClinicianCheckout() {
  const { t } = useTranslation();
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [hasBillingHistory, setHasBillingHistory] = useState(false);
  const [billingHistoryError, setBillingHistoryError] = useState(false);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("checkout") !== "success") {
      return;
    }

    let cancelled = false;
    let timer: number | undefined;
    let attempts = 0;

    setBusy(true);
    setMessage(t("selfServiceCheckout.confirming", {
      defaultValue: "Confirming your subscription...",
    }));

    const reconcile = async () => {
      try {
        const destination = await getProviderPostAuthDestination();
        if (cancelled) return;

        if (destination !== "/register/checkout") {
          if (destination === "/dashboard") {
            await store.dispatch(getProvider()).unwrap();
            if (cancelled) return;
          }
          await router.navigate({ to: destination });
          return;
        }
      } catch {
        if (cancelled) return;
      }

      attempts++;
      if (attempts < 12) {
        timer = window.setTimeout(() => void reconcile(), 1500);
      } else {
        setBusy(false);
        setMessage(t("selfServiceCheckout.pending", {
          defaultValue:
            "Your subscription is not active yet. Check again after payment confirmation.",
        }));
      }
    };

    void reconcile();

    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [router, t]);

  useEffect(() => {
    let cancelled = false;

    void getProviderBillingSummary()
      .then((response) => {
        if (cancelled) return;
        if (response.status !== "success") {
          setBillingHistoryError(true);
          return;
        }
        setHasBillingHistory(response.data.hasBillingHistory);
      })
      .catch(() => {
        if (!cancelled) setBillingHistoryError(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const manageBilling = async () => {
    if (busy) return;
    setBusy(true);
    setMessage("");

    try {
      await openProviderBillingPortal();
    } catch {
      setMessage(t("selfServiceBilling.portalError", {
        defaultValue: "Unable to open billing management. Please try again.",
      }));
    } finally {
      setBusy(false);
    }
  };

  const checkAccess = async (startCheckout: boolean) => {
    if (busy) return;
    setBusy(true);
    setMessage("");

    try {
      const destination = await getProviderPostAuthDestination();
      if (destination !== "/register/checkout") {
        if (destination === "/dashboard") {
          await store.dispatch(getProvider()).unwrap();
        }
        await router.navigate({ to: destination });
        return;
      }

      if (!startCheckout) {
        setMessage(t("selfServiceCheckout.pending", {
          defaultValue: "Your subscription is not active yet. Check again after payment confirmation.",
        }));
        return;
      }

      const response = await createProviderCheckoutSession();
      if (response.status !== "success") {
        throw new Error("Checkout request failed.");
      }

      const url = new URL(response.data.checkoutUrl);
      if (
        url.protocol !== "https:" ||
        url.hostname !== "checkout.stripe.com" ||
        url.username ||
        url.password ||
        url.port
      ) {
        throw new Error("Unexpected checkout destination.");
      }

      window.location.assign(url.href);
    } catch {
      setMessage(t("selfServiceCheckout.error", {
        defaultValue: "Unable to verify your subscription or start checkout. Please try again.",
      }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${bgLinearGradient} min-h-screen`}>
      <div className="flex justify-end p-8"><LanguageSelector /></div>
      <section className="mx-auto max-w-xl px-8 py-12 flex flex-col gap-6">
        <MimiatHorizontalLogo />
        <h1 className="text-3xl font-semibold">
          {t("selfServiceCheckout.title", {
            defaultValue: "Complete your Clinicians+ subscription",
          })}
        </h1>
        <p>{t("selfServiceCheckout.description", {
          defaultValue: "Your clinician profile is ready. Subscribe to activate dashboard access.",
        })}</p>
        <Button
          type="button"
          disabled={busy}
          isLoading={busy}
          onClick={() => void checkAccess(true)}
        >
          {t("selfServiceCheckout.subscribe", {
            defaultValue: "Continue to secure payment",
          })}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={busy}
          onClick={() => void checkAccess(false)}
        >
          {t("selfServiceCheckout.refresh", {
            defaultValue: "Check subscription status",
          })}
        </Button>
        {hasBillingHistory && (
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            isLoading={busy}
            onClick={() => void manageBilling()}
          >
            {t("selfServiceBilling.manage", {
              defaultValue: "Manage subscription and invoices",
            })}
          </Button>
        )}
        {billingHistoryError && (
          <p role="alert" className="text-sm">
            {t("selfServiceBilling.historyError", {
              defaultValue:
                "Unable to load billing history. Refresh this page to retry.",
            })}
          </p>
        )}
        {message && <p role="status" className="text-sm">{message}</p>}
      </section>
    </div>
  );
}
